import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function StatBox({ amount, label, source }: { amount: string; label: string; source: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 2, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#ff9f43', lineHeight: 1, mb: 0.5 }}>{amount}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.6)' }}>{source}</Typography>
    </Box>
  );
}

export default function BlogGuziejkaPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Punkin Doodles: 134 Incidents Before Anyone Noticed" description="How 134 documented abuse incidents at a single daycare went undetected for months — and what behavioral AI pattern detection would have caught instead." path="/blog/punkin-doodles-134-incidents" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Case Study</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff9f43', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Case Study · 8 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          134 Incidents. Three Months. Six Toddlers. How AI Would Have Stopped It on Day One.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          A daycare worker in Lindale, Texas kicked six 2-year-olds 134 times over three months. Surveillance cameras recorded every incident. Not one was flagged until a coworker stumbled across the footage by chance. Here's the scenario that should have happened instead.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        {/* Stats strip */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
          <StatBox amount="134" label="Documented incidents" source="Smith County Sheriff's Office" />
          <StatBox amount="6" label="Victims — all age 2" source="Criminal charges filed" />
          <StatBox amount="3 mo." label="Duration of abuse" source="July – October 2024" />
          <StatBox amount="10 yr" label="Prison sentence" source="November 14, 2025" />
        </Stack>

        <Section title='"134 times."'>
          <Para>
            That's not a typo. Between July and October 2024, a daycare worker named Catherine Guziejka walked up behind 2-year-old children at Punkin' Doodles Day Care in Lindale, Texas — while they sat watching television — and kicked them in the lower back. Swift, deliberate kicks with her right foot. The children cried out. They clutched at where it hurt. And then, after a moment, things went quiet again.
          </Para>
          <Para>
            This happened 134 times. Across six different children. All of them two years old.
          </Para>
          <Para>
            That's not the full picture. Court records and arrest documents describe Guziejka standing on a 2-year-old's left hand for seven seconds while the child tried to pull away. She grabbed sleeping children by the arm and dragged them to changing tables. She pushed toddlers. The abuse was documented across at least 21 separate occasions — a sustained, deliberate pattern spanning a full quarter of the year.
          </Para>
          <Para>
            And every moment of it was on camera.
          </Para>
        </Section>

        <Section title="How it was discovered — and what that tells us">
          <Para>
            This is the part of the story that should keep daycare owners awake at night.
          </Para>
          <Para>
            The abuse wasn't flagged by a system. It wasn't caught by a review protocol. It wasn't discovered by the facility's own safety procedures — in fact, state records show that Punkin' Doodles had previously been cited for failing to have written policies on preventing and responding to child abuse.
          </Para>
          <Para>
            What happened was this: a coworker, for reasons unrelated to any formal review, looked at some security footage. She saw Guziejka kick a 2-year-old boy in the lower back. She told the boy's father. The father called the Smith County Sheriff's Office on October 23, 2024. Deputies then began reviewing the footage themselves — and found 134 incidents going back to July.
          </Para>
          <Para>
            Russell Button, a child injury attorney who followed the case closely and appeared on the Law&Crime Sidebar Podcast to discuss it, put it plainly: the discovery happened by chance. No parent reported anything. No system flagged anything. The footage existed for months, and it took one person happening to look at it — and recognizing what they were seeing — before anything changed.
          </Para>
          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.15)', borderLeft: '3px solid #ff9f43', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              Punkin' Doodles had already been cited by the state.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              State inspection records show the facility was previously found to be lacking written policies on preventing and responding to child abuse — a foundational compliance requirement. The absence of that policy is also the absence of a culture of proactive safety.
            </Typography>
          </Box>
        </Section>

        <Section title="The consequences — for the children, the families, the daycare">
          <Para>
            The six victims were all 2 years old. Physical injuries were documented as minor to medium bruising — the kicks and the standing left marks, but not catastrophic ones. Sheriff Larry Smith noted that they were fortunate none of the children were seriously injured, while adding: "That in no way diminishes the trauma these children endured."
          </Para>
          <Para>
            The trauma is harder to measure. Six two-year-olds were harmed by someone whose job was to protect them. Their parents dropped them off every morning trusting they would be safe. They were not.
          </Para>
          <Para>
            Guziejka was arrested on October 30, 2024. She was charged with six counts of injury to a child with intent to cause bodily injury. She pleaded guilty in September 2025 and was sentenced on November 14, 2025 to ten years in prison — the maximum possible sentence for a third-degree felony in Texas, on each count, to be served concurrently. Assistant District Attorney Richard Vance noted that victim families were "fine with the sentence but frustrated it couldn't be harsher."
          </Para>
          <Para>
            The daycare owner, Melanie Dawn Law, fired Guziejka the same day staff discovered the abuse and immediately reported to CPS and law enforcement. But the damage to the facility was irreversible. After the allegations became public, Punkin' Doodles was sold. A new owner planned to rebrand and reopen under a different name — because the old name now meant something no parent could ignore.
          </Para>
        </Section>

        <Section title="The scenario that should have happened">
          <Para>
            Here's what the morning of July 8, 2024 could have looked like — if SMCLA had been running.
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                time: '9:14 AM',
                event: 'First incident detected',
                detail: "SMCLA's behavioral AI identifies an adult making rapid, forceful contact with a small child from behind. The motion signature — a fast lower-body strike against a toddler — doesn't match any caregiving interaction in the model. An alert is generated.",
              },
              {
                time: '9:14 AM + 47 seconds',
                event: 'Owner notified',
                detail: "A push notification lands on the facility owner's phone. It includes a 12-second timestamped clip — not a request to review hours of footage, but the specific moment, the specific room, the specific child.",
              },
              {
                time: '9:17 AM',
                event: 'Clip reviewed',
                detail: 'The owner watches the clip. What they see is unambiguous. They call the floor supervisor.',
              },
              {
                time: '9:19 AM',
                event: 'Guziejka removed',
                detail: 'Guziejka is pulled from the classroom before the second child has left for the morning. Her shift ends.',
              },
              {
                time: 'The rest of 2024',
                event: '133 incidents never happen',
                detail: 'Six children go home unharmed. Six families never know how close it came. One coworker is never put in the position of stumbling onto evidence of a crime and having to decide what to do with it.',
              },
            ].map(({ time, event, detail }) => (
              <Box key={time} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#ff9f43', fontWeight: 600 }}>{time}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#f0f0f8', fontWeight: 700 }}>{event}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>

          <Para>
            This isn't a hypothetical future. This is what SMCLA's behavioral detection model does today — in real time, across every camera, without anyone having to happen to review footage at the right moment.
          </Para>
        </Section>

        <Section title="Why cameras alone weren't enough">
          <Para>
            The cameras at Punkin' Doodles did their job. They recorded everything. Over 100 incidents were captured on film — the kicks, the hand-standing, the dragging. The footage was there.
          </Para>
          <Para>
            And it sat there for three months.
          </Para>
          <Para>
            This is the fundamental gap that neither traditional security cameras nor basic supervision tools close: the gap between recording and acting. Storage without analysis is an archive of harm, not prevention. It produces evidence. It does not stop the 2nd, 50th, or 134th incident.
          </Para>
          <Para>
            SMCLA doesn't store footage and hope someone reviews it. It watches every frame in real time and acts the moment something is wrong. The difference between those two things is 133 children.
          </Para>
        </Section>

        <Section title="Questions every daycare owner should be able to answer">
          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              'Who at your facility reviews surveillance footage on a daily basis — and what exactly are they trained to look for?',
              'If a staff member was harming children today, how many incidents would it take before you found out?',
              'If discovery depended on a coworker happening to see something, how confident are you that coworker would know what to do?',
              "Your state cited Punkin' Doodles for lacking written abuse-prevention policies. Do you have yours in writing?",
            ].map((q, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#ff9f43', flexShrink: 0, lineHeight: 1.4 }}>{i + 1}.</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.65 }}>{q}</Typography>
              </Box>
            ))}
          </Stack>
          <Para>
            These are not rhetorical. They are the exact questions investigators, licensing boards, and plaintiff attorneys will ask if something goes wrong at your facility. Having answers — systems in place, not just policies on paper — is the difference between a close call and a 10-year story.
          </Para>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'Law&Crime — sentencing and guilty plea coverage', url: 'https://lawandcrime.com/crime/an-ongoing-thing-for-no-reason-day-care-worker-caught-on-video-abusing-toddlers-134-times-over-several-months-is-headed-to-prison/' },
              { label: 'KLTV — original arrest report and incident details', url: 'https://www.kltv.com/2024/11/22/arrest-report-lindale-daycare-worker-caught-camera-kicking-toddlers/' },
              { label: 'CBS19 — sentencing and victim family reaction', url: 'https://www.cbs19.tv/article/news/local/former-east-texas-daycare-worker-sentenced-10-years-pushing-kicking-toddlers/501-d148295a-47a4-4f40-ae72-6e0a640a23a0' },
              { label: 'The Button Law Firm — Russell Button commentary (Law&Crime Sidebar Podcast)', url: 'https://www.buttonlawfirm.com/news/blf-comments-on-guilty-plea-in-punkin-doodles-daycare-abuse-case.cfm' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#5ce1e6', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            See how SMCLA would work in your facility
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            134 incidents over three months were recorded and missed. One conversation can show you exactly how that gap gets closed — in your rooms, with your cameras, in real time.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
